<template>
  <header :class="{ darkHeader: darkMode }">
    <nav class="navbar">
      <!-- Píldora izquierda: logo de marca (enlaza al inicio) -->
      <router-link to="/" class="pill pill-logo" aria-label="Inicio — Selk">
        <img
          :src="darkMode ? require('../assets/SELK_LOGO_DARK.svg') : require('../assets/SELK_LOGO.svg')"
          alt="Selk"
        />
      </router-link>

      <!-- Botón hamburguesa (solo móvil) -->
      <button
        class="pill pill-toggle"
        :aria-expanded="isMenuOpen"
        aria-label="Abrir menú"
        @click="toggleMenu"
      >
        <span class="material-symbols-outlined">{{ isMenuOpen ? 'close' : 'menu' }}</span>
      </button>

      <!-- Píldora derecha: enlaces de navegación -->
      <div class="pill pill-links" :class="{ open: isMenuOpen }">
        <ul>
          <li v-for="link in navLinks" :key="link.label" @click="closeMenu">
            <!-- Los enlaces con ancla comparten ruta (/services): sin esto, al
                 entrar a Servicios se marcaban dos áreas como activas a la vez. -->
            <router-link
              :to="link.to"
              class="nav-link"
              :exact-active-class="typeof link.to === 'string' ? 'router-link-exact-active' : 'nav-link-anchor'"
            >{{ link.label }}</router-link>
          </li>
        </ul>
        <router-link
          to="/contact"
          class="nav-cta"
          aria-label="Solicita tu cotización"
          @click="closeMenu"
        >Cotiza ahora</router-link>
      </div>
    </nav>
  </header>
</template>

<script>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'HeaderPage',
  setup() {
    const route = useRoute();
    const darkMode = computed(() => route.path === '/onibi');

    // Navegación por las tres áreas clave (plan 22_08_2026, R1). El logo cubre
    // el enlace a Inicio; /onibi sale del menú comercial (ruta viva, sin enlace).
    const navLinks = [
      { to: '/print3d', label: 'Impresión 3D' },
      // Acceso directo al catálogo completo: es la vía de compra más corta y
      // antes sólo se llegaba bajando hasta el carrusel de /print3d.
      { to: '/print3d/catalogo', label: 'Catálogo' },
      { to: { path: '/services', hash: '#area-automatizacion' }, label: 'Automatización' },
      { to: { path: '/services', hash: '#area-aplicativos' }, label: 'Aplicativos web' },
      { to: '/us', label: 'Nosotros' },
      { to: '/contact', label: 'Contacto' },
    ];

    const isMenuOpen = ref(false);
    const toggleMenu = () => { isMenuOpen.value = !isMenuOpen.value; };
    const closeMenu = () => { isMenuOpen.value = false; };

    return { darkMode, navLinks, isMenuOpen, toggleMenu, closeMenu };
  },
};
</script>

<style scoped>
header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 4;
  display: flex;
  justify-content: center;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
  pointer-events: none; /* deja pasar el scroll fuera de las píldoras */
}

.navbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 1100px;
}

/* Superficie compartida de píldora (manualMarca.md: superficie #F8F8F8 + borde #9DB2BF) */
.pill {
  pointer-events: auto;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  box-shadow: 0px 8px 20px rgba(157, 178, 191, 0.25);
  box-sizing: border-box;
}

.pill-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  padding: 0 1.65rem;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.pill-logo:hover {
  transform: translateY(-2px);
  box-shadow: 0px 12px 25px rgba(157, 178, 191, 0.45);
}
.pill-logo img {
  height: 56px;
  width: auto;
  display: block;
}

.pill-links {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  height: 78px;
  padding: 0 1.5rem;
}
.pill-links ul {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin: 0;
  padding: 0;
}
.pill-links li { line-height: 1; }
.pill-links li::marker { content: ''; }

.nav-link {
  font-family: var(--font-body);
  font-size: 19px;
  text-decoration: none;
  color: var(--color-text);
  padding-bottom: 2px;
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
}
.nav-link:hover {
  color: var(--color-text-strong);
  border-bottom-color: var(--color-brand);
}
/* Estado activo de ruta: acento de marca */
.nav-link.router-link-exact-active {
  color: var(--color-brand);
  border-bottom-color: var(--color-brand);
}

/* CTA dentro de la píldora */
.nav-cta {
  font-family: var(--font-body);
  font-size: 19px;
  text-decoration: none;
  color: var(--color-white);
  background-color: var(--color-action);
  border-radius: 9999px;
  padding: 0.6rem 1.4rem;
  transition: background-color 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}
.nav-cta:hover {
  background-color: var(--color-action-hover);
  transform: translateY(-2px);
}

/* Toggle móvil (oculto en escritorio) */
.pill-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  cursor: pointer;
  color: var(--color-text-strong);
}
.pill-toggle .material-symbols-outlined { font-size: 42px; }

/* ---------- Modo oscuro (ruta /onibi) ---------- */
.darkHeader .pill {
  background-color: var(--color-dark-surface);
  border-color: rgba(255, 255, 255, 0.15);
}
.darkHeader .nav-link { color: rgba(255, 255, 255, 0.85); }
.darkHeader .nav-link:hover,
.darkHeader .nav-link.router-link-exact-active {
  color: var(--color-white);
  border-bottom-color: var(--color-brand);
}
.darkHeader .pill-toggle { color: var(--color-white); }

/* ---------- Responsive ----------
   El menú colapsa a hamburguesa en ≤1200px: con las tres áreas más el acceso
   al catálogo son seis enlaces, y por debajo de ese ancho ya no caben en una
   sola píldora (antes el umbral era 1024px con cinco). */
@media (max-width: 1200px) {
  header { padding: 0.75rem 1rem; }
  .navbar { justify-content: space-between; gap: 0.5rem; }

  .pill-toggle { display: flex; order: 2; }
  .pill-logo { order: 1; height: 69px; }
  .pill-logo img { height: 46px; }

  .pill-links {
    order: 3;
    position: absolute;
    top: 96px;
    left: 1rem;
    right: 1rem;
    height: auto;
    flex-direction: column;
    gap: 1rem;
    padding: 1.2rem 1.4rem;
    border-radius: 24px;
    display: none;
  }
  .pill-links.open { display: flex; }
  .pill-links ul {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    align-items: center;
  }
  .nav-cta { width: 100%; text-align: center; }
}
</style>
